import { keepPreviousData } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { UseQueryResult } from "@tanstack/react-query";
import isEmpty from "lodash/isEmpty";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { PER_PAGE_UNPAGINATED } from "PFApp/constants/unpaginated";
import { fetchProjectsEngagements } from "PFCore/services/booking/fetch_projects_engagements";
import { fetchProjectsRoles } from "PFCore/services/booking/fetch_projects_roles";
import { QueryOptions } from "PFTypes";

import { projectsKeys } from "./query_keys";
import { ProjectsResponse } from "./use_projects_entries";

const EMPTY_RESPONSE = { entries: [], meta: { total: 0, totalPages: 0, perPage: 0, page: 0 } };
const DEFAULT_ORDER = { name: ["activity.saas_created_at"], text: "Latest first", direction: "desc" };
const getActivityFilters = (pinnedActivityIds: number[]) => ({
  fields: { id: pinnedActivityIds }
});

export type UsePinnedProjects = {
  pinnedIds: number[];
  calendarRoot: CalendarRoot;
  options: QueryOptions<ProjectsResponse, { status: number }>;
};

export const usePinnedProjects = ({
  pinnedIds,
  calendarRoot,
  options = {}
}: UsePinnedProjects): UseQueryResult<ProjectsResponse, { status: number }> => {
  const filters = getActivityFilters(pinnedIds);

  return useQuery<ProjectsResponse, { status: number }>({
    queryKey: projectsKeys.pinned(calendarRoot, filters, DEFAULT_ORDER, 1, PER_PAGE_UNPAGINATED),
    queryFn: () => {
      // In case activity id filter is empty it returns all entries, but we want otherwise.
      // Setting enable=false doesn't solve the issue because then keepPreviousData remembers wrong prev data.
      if (isEmpty(pinnedIds)) {
        return Promise.resolve(EMPTY_RESPONSE);
      }
      const params = { page: 1, perPage: PER_PAGE_UNPAGINATED, order: DEFAULT_ORDER, filters };
      return calendarRoot === CalendarRoot.ProjectsRoles
        ? fetchProjectsRoles(params)
        : fetchProjectsEngagements(params);
    },
    retry: false,
    placeholderData: keepPreviousData,
    ...options
  });
};
